import type { Enums } from "@bednbody/types/database";

export type StaffRole = Exclude<Enums<"user_role">, "client" | "therapist" | "rider">;

export interface BednbodyClaims {
  role: StaffRole;
  hub_id: string | null;
}

const ADMIN_ROLES: readonly StaffRole[] = ["hub_manager", "hr", "admin", "executive", "superadmin"];

function isStaffRole(value: unknown): value is StaffRole {
  return typeof value === "string" && (ADMIN_ROLES as readonly string[]).includes(value);
}

/**
 * Reads `role` + `hub_id` off the access token's JWT payload.
 *
 * These claims are set by the `custom_access_token` Postgres function (auth
 * hook — see migration 0014/0021) and are what Postgres RLS actually checks.
 * Decoding them here is for UI gating only (nav visibility, route guards) —
 * it is NOT the security boundary. The real boundary is RLS at the DB layer;
 * never treat a client-side claims check as sufficient authorization.
 */
export function decodeBednbodyClaims(accessToken: string): BednbodyClaims | null {
  try {
    const payload = decodeJwtPayload(accessToken);
    if (!payload) return null;

    const role = payload.role ?? payload.user_role;
    if (!isStaffRole(role)) return null;

    const hubId = typeof payload.hub_id === "string" ? payload.hub_id : null;

    return { role, hub_id: hubId };
  } catch {
    return null;
  }
}

export function isHubScoped(role: StaffRole): boolean {
  return role === "hub_manager";
}

/**
 * Reads the `aal` claim off the access token. Synchronous on purpose: gating
 * on `mfa.getAuthenticatorAssuranceLevel()` (async) races the post-MFA
 * redirect — ProtectedRoute would see a stale aal1 and bounce a freshly
 * verified session back to /login.
 */
export function decodeAal(accessToken: string): "aal1" | "aal2" | null {
  const payload = decodeJwtPayload(accessToken);
  const aal = payload?.aal;
  return aal === "aal1" || aal === "aal2" ? aal : null;
}

function decodeJwtPayload(accessToken: string): Record<string, unknown> | null {
  try {
    const payloadSegment = accessToken.split(".")[1];
    if (!payloadSegment) return null;

    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}
